import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureUser } from '../../util/data';
import { REVIEW_TYPE_OF_PROVIDER, propTypes } from '../../util/types';
import { getSellerBundleOffer, getSellerShopName } from '../../util/userHelpers';
import { followUser, getFollowSummary, unfollowUser } from '../../util/api';
import {
  AvatarLarge,
  H1,
  NamedLink,
  InlineTextButton,
  OutsideClickHandler,
  UserBadgeList,
} from '../../components';

import css from './ShopHeader.module.css';

/**
 * ShopHeader component displays the storefront header with shop name, avatar,
 * rating, and action buttons.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.user} props.user - The shop/seller user
 * @param {propTypes.currentUser} props.currentUser - The current user viewing
 * @param {Array<propTypes.review>} props.reviews - Reviews of the seller
 * @param {Function} props.onContactUser - Callback to initiate contact
 * @param {boolean} props.isCurrentUser - Is this the current user's shop
 * @returns {JSX.Element}
 */
const ShopHeader = props => {
  const { user, currentUser, reviews = [], listings = [], onContactUser, isCurrentUser } = props;

  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [showListingTypeChoice, setShowListingTypeChoice] = useState(false);
  const [shareState, setShareState] = useState('idle');
  const [followSummary, setFollowSummary] = useState(null);
  const [followInProgress, setFollowInProgress] = useState(false);
  const [followError, setFollowError] = useState(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
  }, []);

  const sellerId = user?.id?.uuid;
  const viewerId = currentUser?.id?.uuid;
  const viewerOwnsShop = Boolean(sellerId && viewerId && sellerId === viewerId);
  useEffect(() => {
    let active = true;
    if (!sellerId) return undefined;
    getFollowSummary(sellerId)
      .then(summary => {
        if (active) setFollowSummary(summary);
      })
      .catch(() => {
        if (active) setFollowSummary(null);
      });
    return () => {
      active = false;
    };
  }, [sellerId]);

  const ensuredUser = ensureUser(user);
  const { displayName, publicData = {} } = ensuredUser?.attributes?.profile || {};
  const shopName = getSellerShopName(ensuredUser, displayName);
  const bundleOffer = getSellerBundleOffer(ensuredUser);
  const province = publicData.province;

  const providerReviews = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewCount = providerReviews.length;
  const averageRating =
    providerReviews.length > 0
      ? (
          providerReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) /
          providerReviews.length
        ).toFixed(1)
      : null;

  const memberSinceDate = new Date(ensuredUser?.attributes?.createdAt);
  const memberSinceYear = memberSinceDate.getFullYear();
  const profileUrl = typeof window !== 'undefined' ? window.location.href : '';
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: shopName || displayName, url: profileUrl });
      } else if (navigator.clipboard && profileUrl) {
        await navigator.clipboard.writeText(profileUrl);
        setShareState('copied');
      }
    } catch (error) {
      setShareState('idle');
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      history.push('/signup', {
        from: `${location.pathname}${location.search}${location.hash}`,
      });
      return;
    }
    if (!sellerId || viewerOwnsShop || followInProgress) return;

    const wasFollowing = Boolean(followSummary?.isFollowing);
    const previousSummary = followSummary;
    setFollowInProgress(true);
    setFollowError(false);
    setFollowSummary(summary => ({
      ...(summary || { followers: 0, following: 0 }),
      isFollowing: !wasFollowing,
      followers: Math.max(0, (summary?.followers || 0) + (wasFollowing ? -1 : 1)),
    }));
    try {
      const summary = wasFollowing ? await unfollowUser(sellerId) : await followUser(sellerId);
      setFollowSummary(summary);
    } catch (error) {
      setFollowSummary(previousSummary);
      if (error?.code === 'authentication_required') {
        // The session seen by the local API has expired even if the page still shows the user;
        // redirect to sign-in instead of showing a misleading retry message.
        history.push('/login', {
          from: `${location.pathname}${location.search}${location.hash}`,
        });
        return;
      }
      setFollowError(true);
    } finally {
      setFollowInProgress(false);
    }
  };

  return (
    <div className={css.root}>
      <div className={css.cover} aria-hidden="true" />
      <div className={css.profileRow}>
        <div className={css.avatarSection}>
          <AvatarLarge className={css.avatar} user={ensuredUser} disableProfileLink />
          {isCurrentUser ? (
            <NamedLink
              className={css.editAvatarLink}
              name="ProfileSettingsPage"
              ariaLabel={intl.formatMessage({ id: 'ShopHeader.editProfileIcon' })}
            >
              <FormattedMessage id="ShopHeader.editProfileIcon" />
            </NamedLink>
          ) : null}
        </div>

        <div className={css.contentSection}>
          <div className={css.headerRow}>
            <div className={css.titleSection}>
              <H1 className={css.shopName}>{shopName || displayName}</H1>
              <UserBadgeList user={ensuredUser} />
              {province ? (
                <p className={css.province}>
                  <FormattedMessage id="ShopHeader.shipsFrom" values={{ province }} />
                </p>
              ) : null}
            </div>

            {mounted ? (
              <div className={css.actionSection}>
                {!viewerOwnsShop ? (
                  <button
                    type="button"
                    className={classNames(css.secondaryAction, {
                      [css.followingAction]: followSummary?.isFollowing,
                    })}
                    onClick={handleFollow}
                    disabled={followInProgress}
                    aria-pressed={Boolean(followSummary?.isFollowing)}
                  >
                    {followInProgress
                      ? 'Loading...'
                      : followSummary?.isFollowing
                      ? 'Following'
                      : 'Follow'}
                  </button>
                ) : null}
                {followError ? (
                  <span className={css.followError} role="alert">
                    {intl.formatMessage({ id: 'ShopHeader.followFailed' })}
                  </span>
                ) : null}
                <button type="button" className={css.secondaryAction} onClick={handleShare}>
                  {shareState === 'copied' ? 'Link copied' : 'Share'}
                </button>
                {viewerOwnsShop ? (
                  <OutsideClickHandler
                    className={css.createListingWrapper}
                    onOutsideClick={() => setShowListingTypeChoice(false)}
                  >
                    <button
                      type="button"
                      className={css.createListingButton}
                      aria-haspopup="true"
                      aria-expanded={showListingTypeChoice}
                      onClick={() => setShowListingTypeChoice(open => !open)}
                    >
                      <FormattedMessage id="ShopHeader.createListing" />
                    </button>
                    {showListingTypeChoice ? (
                      <div className={css.listingTypeChoice} role="menu">
                        <p className={css.listingTypeChoiceLabel}>What are you listing?</p>
                        <NamedLink
                          className={css.listingTypeOption}
                          name="NewListingPage"
                          to={{ search: '?listingType=book-listing' }}
                        >
                          Book
                        </NamedLink>
                        <NamedLink
                          className={css.listingTypeOption}
                          name="NewListingPage"
                          to={{ search: '?listingType=collector-merch' }}
                        >
                          Merchandise
                        </NamedLink>
                      </div>
                    ) : null}
                  </OutsideClickHandler>
                ) : onContactUser ? (
                  <InlineTextButton className={css.messageButton} onClick={onContactUser}>
                    <FormattedMessage id="ShopHeader.messageSeller" />
                  </InlineTextButton>
                ) : null}
              </div>
            ) : null}
          </div>

          {averageRating !== null || memberSinceYear ? (
            <div className={css.metaSection}>
              {averageRating !== null ? (
                <div className={css.ratingBlock}>
                  <span className={css.rating}>{averageRating}</span>
                  <span className={css.reviewCount}>
                    {intl.formatMessage({ id: 'ShopHeader.reviewCount' }, { count: reviewCount })}
                  </span>
                </div>
              ) : null}
              {memberSinceYear ? (
                <p className={css.memberSince}>
                  <FormattedMessage
                    id="ShopHeader.memberSince"
                    values={{ year: memberSinceYear }}
                  />
                </p>
              ) : null}
            </div>
          ) : null}

          <div className={css.stats}>
            <strong>{listings.length}</strong>
            <span>active listings</span>
          </div>
          {followSummary ? (
            <div className={css.followStats}>
              <span>
                <strong>{followSummary.followers}</strong> Followers
              </span>
              <span>
                <strong>{followSummary.following}</strong> Following
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {bundleOffer ? (
        <div className={css.benefitStrip}>
          <span className={css.benefitIcon} aria-hidden="true">
            %
          </span>
          <span>{bundleOffer}</span>
        </div>
      ) : null}
    </div>
  );
};

ShopHeader.propTypes = {
  user: propTypes.user.isRequired,
  currentUser: propTypes.currentUser,
  reviews: PropTypes.arrayOf(propTypes.review),
  listings: PropTypes.arrayOf(propTypes.listing),
  onContactUser: PropTypes.func,
  isCurrentUser: PropTypes.bool,
};

ShopHeader.defaultProps = {
  reviews: [],
  listings: [],
  isCurrentUser: false,
};

export default ShopHeader;
